class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('change', this.handlePolicyChange.bind(this));
    this.addEventListener('click', this.handleCheckoutClick.bind(this));
    this.addEventListener('submit', this.handleCheckoutSubmit.bind(this));
    this.setHeaderCartIconAccessibility();
    this.syncPolicyCheckoutState();
  }

  isPolicyAccepted() {
    const policyCheckbox = this.querySelector('#policy-checkbox');
    return !policyCheckbox || policyCheckbox.checked;
  }

  syncPolicyCheckoutState() {
    const policyAccepted = this.isPolicyAccepted();
    const cartIsEmpty = this.classList.contains('is-empty');

    this.querySelectorAll('button[name="checkout"]').forEach((checkoutButton) => {
      checkoutButton.disabled = !policyAccepted || cartIsEmpty;
    });
  }

  handlePolicyChange(event) {
    if (!event.target.matches('#policy-checkbox')) return;
    this.syncPolicyCheckoutState();
  }

  handleCheckoutClick(event) {
    if (!event.target.closest('button[name="checkout"]') || this.isPolicyAccepted()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  handleCheckoutSubmit(event) {
    if (event.submitter?.name !== 'checkout' || this.isPolicyAccepted()) return;
    event.preventDefault();
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll(
      '#cart-icon-bubble, #cart-icon-bubble-sticky , #cart-notification-button',
    );
    if (!cartLinks.length) return;
    cartLinks.forEach((icon) => this.addDrawerOpenHandler(icon));
  }

  addDrawerOpenHandler(icon) {
    if (!icon) return;

    icon.setAttribute('role', 'button');
    icon.setAttribute('aria-haspopup', 'dialog');
    icon.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(icon);
    });
    icon.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(icon);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    if (MOUSE_CURSOR) MOUSE_CURSOR.hide();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, { open = true } = {}) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    if (parsedState && parsedState.id) {
      this.productId = parsedState.id;
    }

    this.getSectionsToRender()?.forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState?.sections) {
        return;
      }

      const sectionHTML = parsedState.sections[section.id];
      if (!sectionHTML) {
        return;
      }

      const nextInnerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);

      if (typeof nextInnerHTML !== 'string') {
        return;
      }

      sectionElement.innerHTML = nextInnerHTML;
    });

    this.syncPolicyCheckoutState();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));

      if (open) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
      {
        id: 'cart-icon-bubble-sticky',
        selector: '#cart-icon-bubble-sticky',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-icon-bubble-sticky',
        section: 'cart-icon-bubble-sticky',
        selector: '#cart-icon-bubble-sticky',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
